import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { FormGroup } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { ModuleAcceessService } from './moduleAccess.service';
import { BehaviorSubject } from 'rxjs';
import { EmbedService } from '../features/embed/embed.service';
interface ClubData {
  _id: string;
  club: string;
}
// export interface Club {
//   clubId: string;
//   clubName: string;
// }
@Injectable({ providedIn: 'root' })
export class CommonService {

  // ----------------------------------
  // Layout State
  // ----------------------------------
  public loginlayout = { layout: 'login' };
  public menu = { 
    isMini: /Android|webOS|iPhone|iPad|iPod|BlackBerry|Windows Phone/i.test(
    navigator.userAgent
  )
   };
  public showDashboard = true;

  // ----------------------------------
  // CKEditor Config
  // ----------------------------------
  public ckEditorConfig = {
    uiColor: '#F0F3F4',
    height: '350',
    extraPlugins: 'divarea',
    toolbar: [
      { name: 'clipboard', items: ['Cut', 'Copy', 'Paste', 'PasteText', 'PasteFromWord', '-', 'Undo', 'Redo'] },
      { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Strike', 'Subscript', 'Superscript', '-', 'RemoveFormat'] },
      { name: 'paragraph', items: ['NumberedList', 'BulletedList', '-', 'Outdent', 'Indent', 'CreateDiv', '-', 'Blockquote'] },
      { name: 'justify', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] },
      { name: 'styles', items: ['Styles', 'Format', 'FontSize', '-', 'TextColor', 'BGColor'] },
    ]
  };

  // ----------------------------------
  // Permission Management
  // ----------------------------------
  private permissionUser = new BehaviorSubject<Record<string, boolean>>({});
  permissions$ = this.permissionUser.asObservable();

  constructor(
    private router: Router,
    private toastr: ToastrService,
    private http: HttpClient,
    private module: ModuleAcceessService,
    private embedService:EmbedService
  ) {}

  // ----------------------------------
  // Navigation Helpers
  // ----------------------------------
  navigateTo(url: string): void {
    this.router.navigate([url]);
  }

  // ----------------------------------
  // Form Helpers
  // ----------------------------------
  markAsTouchedFormControls(form: FormGroup): void {
    Object.values(form.controls).forEach(control =>
      control?.markAsTouched({ onlySelf: true })
    );
  }

  resetForm(formGroup: FormGroup): void {
    formGroup.reset();
    formGroup.markAsUntouched();

    Object.values(formGroup.controls).forEach(control => {
      control.setErrors(null);
    });
  }

  // ----------------------------------
  // Permissions
  // ----------------------------------
  setPermissions(permissions: any): void {
    this.permissionUser.next(permissions || {});
  }

  getPermissions(): Record<string, boolean> {
    return this.permissionUser.getValue();
  }

  hasPermission(code: string): boolean {
    if (!code) return true;
    return this.getPermissions().hasOwnProperty(code);
  }

  // ----------------------------------
  // Club Permissions
  // ----------------------------------
  async clubPermission(): Promise<string[]> {
    const userPermissions = this.getPermissions();
    const allowedClubs: string[] = [];

    return new Promise((resolve, reject) => {
      this.getClubDetail().subscribe({
        next: (res: any) => {
          if (!res?.data) return reject(false);
          res.data.forEach((club: ClubData) => {
            const clubCode = club?.club?.trim()?.toLowerCase();
            if (!clubCode) return; 
            const key = `${clubCode}_club`;
            if (userPermissions[key]) {
              allowedClubs.push(club._id);
            }
          });

          resolve(allowedClubs);
        },
        error: () => reject(false)
      });
    });
  }

  // ----------------------------------
  // Menu Access
  // ----------------------------------
  isMenuAccessible(): any[] {
    const modules = this.module.getModuleInfo();
    const permittedMenu: any[] = [];
    modules.forEach(menu => {
      const permissionCode = `list_${menu.name.toLowerCase()}`;
      if (this.hasPermission(permissionCode)) {
        permittedMenu.push(menu);
      }
    });
    return permittedMenu;
  }

  // ----------------------------------
  // API Calls
  // ----------------------------------
  getClubDetail() {
    return this.http.post("/videoListing/clubList", {});
  }

  // ----------------------------------
  // File Helpers
  // ----------------------------------
  getBase64(file: Blob): Promise<string | ArrayBuffer | null> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
      reader.readAsDataURL(file);
    });
  }

  // ----------------------------------
  // Utility Helpers
  // ----------------------------------
  deepCopy<T>(value: T): T {
    return value == null ? value : JSON.parse(JSON.stringify(value));
  }

  retrieve(key: string): any {
    return localStorage.getItem(key);
  }

  // ----------------------------------
  // Toast Helpers
  // ----------------------------------
  showSuccess(title: string, message: string): void {
    this.toastr.success(message, title);
  }

  showInfo(title: string, message: string): void {
    this.toastr.info(message, title);
  }

  showError(title: string, message: string): void {
    this.toastr.error(message, title);
  }

  showWarning(title: string, message: string): void {
    this.toastr.warning(message, title);
  }

  // ----------------------------------
  // Mapping
  // ----------------------------------
  mapClub(club: string): string {
    const clubNameMap: Record<string, string> = {
      wolves: 'Wolves',
      epl: 'Epl',
      chl: 'CHL',
      clubbrugge: 'Clubbrugge',
      fulham: 'Fulham',
      wolves_u23: 'Wolves_U23',
    };

    return clubNameMap[club] ?? club;
  }
}
